import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  OnDestroy,
  OnInit,
  ViewChild,
  ChangeDetectorRef
} from "@angular/core";
import { FormControl, FormGroup, NonNullableFormBuilder, Validators } from "@angular/forms";
import { MatStepper } from "@angular/material/stepper";
import { MatSlideToggleChange } from "@angular/material/slide-toggle";
import { MatRadioChange } from "@angular/material/radio";
import { BehaviorSubject, Subscription } from "rxjs";
import { distinctUntilChanged, map, filter, take } from "rxjs/operators";
import { PhoneService } from "@onsip/common/services/api/resources/phone/phone.service";
import { OrganizationService } from "@onsip/common/services/api/resources/organization/organization.service";
import {
  getModelFeatures,
  getPhoneMakes,
  getPhoneModels,
  DisplayPhoneModel,
  PhoneMake,
  PhoneFeatures
} from "@onsip/common/interfaces/phone-info";
import { Phone, PhoneAddRequestParams } from "@onsip/common/services/api/resources/phone/phone";
import timezones from "../../../../../common/interfaces/timezones";
import { views } from "@onsip/web/app/phone/views";
import { SnackbarService } from "../../../shared/components/snackbar/snackbar.service";
import { LineInfo } from "../phoneDetail/phone-detail.component";
import { LineOptionsType } from "../phoneLines/phone-lines-edit.component";
import { ParameterValue } from "@onsip/common/services/api/util/api-action-description";
import { TranslateService } from "@ngx-translate/core";
import { MatDialog } from "@angular/material/dialog";
import { ModalMaterialComponent } from "../../../shared/components/modal/modal-material.component";
import { PackageService } from "@onsip/common/services/api/resources/package/package.service";
import { AccountResourceService } from "../../../../../common/services/api/resources/package/account-resource.service";

@Component({
  selector: "onsip-phone-setup",
  templateUrl: "./phone-setup.component.html",
  styleUrls: ["./phone-setup.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class PhoneSetupComponent implements OnInit, OnDestroy {
  @HostBinding("class.onsip-grid-content")
  @HostBinding("class.onsip-admin-host-grid")
  _dontUse = true;
  @ViewChild("stepper") stepper!: MatStepper;
  views = views;
  mobileBreakpoint = this.breakpointObserver
    .observe(Breakpoints.XSmall)
    .pipe(map(result => result.matches));

  timezoneOptions = timezones.timezoneOptions;
  phoneMakes = getPhoneMakes();
  /** we store the phone model key in form and display the name to the user */
  phoneModel: Array<DisplayPhoneModel> = [];
  expansionModuleName = "";
  /** list of button options for lines: both means line can be switch between phone and blf */
  lineOptions: Array<LineOptionsType> = [];
  macAddressMaxLength = 17;

  loadingStateComplete$ = new BehaviorSubject(true);

  formGroup = this.fb.group({
    // set default make to polycom
    make: this.fb.control(PhoneMake.Polycom),
    model: this.fb.control(""),
    macAddress: this.fb.control(""),
    // set initial time to be eastern time - this will be more familiar for our US customers
    timezone: this.fb.control("-5"),
    orgPassword: this.fb.control(false),
    directory: this.fb.control(false),
    intercom: this.fb.control(false),
    natKeepalive: this.fb.control(false),
    easyTransfer: this.fb.control(false),
    webInterface: this.fb.control(false),
    endToEndEncryption: this.fb.control(false),
    digitMap: this.fb.control(""),
    blf: this.fb.control(false),
    expansionModule: this.fb.control(false),
    linesInfo: this.fb.group({})
  });

  phoneFeatures!: PhoneFeatures;
  /** flag if the phone selected has no advanced features - we show a specific message for this */
  noFeatures = false;
  /** cache to help us retrieve the newly created phone */
  private phoneCache!: Record<string, Phone>;
  /** phone id of newly create phone - we need this to assign lines to the phone */
  private newPhoneId!: string;
  /** track number of total lines being used including expansion modules */
  private totalLines = 0;
  private firstLineAssigned = false;
  private unsubscriber = new Subscription();
  userUnlimited = false;
  blfPricing!: String;

  constructor(
    private fb: NonNullableFormBuilder,
    private breakpointObserver: BreakpointObserver,
    private dialog: MatDialog,
    private orgService: OrganizationService,
    private phoneService: PhoneService,
    private snackbar: SnackbarService,
    private translate: TranslateService,
    private packageService: PackageService,
    private cdRef: ChangeDetectorRef,
    private resourceService: AccountResourceService
  ) {}

  ngOnInit() {
    this.unsubscriber.add(
      this.phoneService.orgState.subscribe(phones => {
        if (!this.phoneCache) {
          this.phoneCache = {};
          phones.forEach(phone => (this.phoneCache[phone.phoneId] = phone));
        } else {
          phones.forEach(phone => {
            if (!this.phoneCache[phone.phoneId]) {
              this.phoneCache[phone.phoneId] = phone;
              this.newPhoneId = phone.phoneId;
            }
          });
        }
      })
    );

    this.unsubscriber.add(
      this.orgService.orgState
        .pipe(map(org => org[0].busyLampField))
        .subscribe(hasBLF => this.formGroup.controls.blf.setValue(hasBLF))
    );

    this.unsubscriber.add(
      this.formGroup.controls.make.valueChanges.pipe(distinctUntilChanged()).subscribe(value => {
        this.phoneModel = getPhoneModels(value);
        // set formcontrol model to the first model in the list so we dont have an awkward empty field
        this.formGroup.controls.model.setValue(this.phoneModel[0].key);
      })
    );

    this.unsubscriber.add(
      this.formGroup.controls.model.valueChanges
        .pipe(
          distinctUntilChanged(),
          filter(value => !!value)
        )
        .subscribe(value => {
          this.phoneFeatures = getModelFeatures(this.formGroup.controls.make.value, value);
          // set an expansion module name if there is a side car for phoneFeature
          this.expansionModuleName = this.phoneFeatures.SideCar
            ? this.phoneFeatures.SideCar[0].Type
            : "";
          this.formGroup.controls.expansionModule.patchValue(false);
          this.cdRef.detectChanges();

          // disable features option if feature is not available and enable feature option if they are available
          if (this.phoneFeatures.WebPassword) {
            this.formGroup.controls.orgPassword.enable();
          } else {
            this.formGroup.controls.orgPassword.disable();
          }
          if (this.phoneFeatures.Directory) {
            this.formGroup.controls.directory.enable();
          } else {
            this.formGroup.controls.directory.disable();
          }
          if (this.phoneFeatures.Intercom) {
            this.formGroup.controls.intercom.enable();
          } else {
            this.formGroup.controls.intercom.disable();
          }
          if (this.phoneFeatures.NATKeepalive) {
            this.formGroup.controls.natKeepalive.enable();
          } else {
            this.formGroup.controls.natKeepalive.disable();
          }
          if (this.phoneFeatures.EasyTransfer) {
            this.formGroup.controls.easyTransfer.enable();
          } else {
            this.formGroup.controls.easyTransfer.disable();
          }
          if (this.phoneFeatures.WebInterface) {
            this.formGroup.controls.webInterface.enable();
          } else {
            this.formGroup.controls.webInterface.disable();
          }
          if (this.phoneFeatures.EndToEndEncryption) {
            this.formGroup.controls.endToEndEncryption.enable();
          } else {
            this.formGroup.controls.endToEndEncryption.disable();
          }
          if (this.phoneFeatures.DigitMap) {
            this.formGroup.controls.digitMap.enable();
          } else {
            this.formGroup.controls.digitMap.disable();
          }

          this.noFeatures =
            !this.phoneFeatures.Directory &&
            !this.phoneFeatures.Intercom &&
            !this.phoneFeatures.NATKeepalive &&
            !this.phoneFeatures.EasyTransfer &&
            !this.phoneFeatures.WebInterface &&
            !this.phoneFeatures.EndToEndEncryption &&
            !this.phoneFeatures.DigitMap;

          // create formControls for model's lines
          this.getDefaultLinesFormControl();
        })
    );

    this.unsubscriber.add(
      this.formGroup.controls.macAddress.valueChanges.subscribe((value: string) => {
        // mac address can come in three different formats
        // 1. without any delimiters so max length of mac address is 12 digits
        if (value.match(/^[0-9a-fA-F]{12}$/)) {
          this.macAddressMaxLength = 12;
          // 2. mac address can be in triplets of four digits separated by periods. This gives a max length of 14 characters
        } else if (value.match(/^([0-9a-fA-F]{4}\.[0-9a-fA-F]{4}\.[0-9a-fA-F]{4})$/)) {
          this.macAddressMaxLength = 14;
          // 3. Lastly, mac address can be in six groups of digits separated by colon or dashes. This gives a max length of 17 characters
        } else {
          this.macAddressMaxLength = 17;
        }
      })
    );

    this.unsubscriber.add(
      this.formGroup.controls.expansionModule.valueChanges.subscribe(value => {
        if (value) {
          this.addExpansionFormControls();
        } else {
          this.removeExpansionFormControls();
        }
      })
    );

    this.unsubscriber.add(
      this.formGroup.valueChanges.subscribe(() => {
        this.checkDuplicateLinesFormGroup();
        this.getLinesFormGroupErrorState();

        if (
          this.linesInfo.controls.nameLine1 &&
          this.linesInfo.controls.nameLine1.value &&
          !this.firstLineAssigned
        ) {
          this.firstLineAssigned = true;
          this.setLinesEnabled();
        } else if (this.firstLineAssigned && this.allLinesUnassigned()) {
          this.firstLineAssigned = false;
          this.setLinesDisabled();
        }
      })
    );
    this.unsubscriber.add(
      this.packageService.getAssignedPrimaryPackages().subscribe(packages => {
        packages.map(pkg => {
          if (pkg.status === "user") {
            this.userUnlimited = true;
          }
        });
        this.cdRef.markForCheck();
      })
    );

    // set model after valueChange observables have been created so they get updated properly
    this.phoneModel = getPhoneModels(this.formGroup.controls.make.value);
    // set formcontrol model to the first model in the list so we dont have an awkward empty field
    this.formGroup.controls.model.setValue(this.phoneModel[0].key);

    this.getPricing();
  }

  ngOnDestroy() {
    this.unsubscriber.unsubscribe();
  }

  get linesInfo(): FormGroup {
    return this.formGroup.controls.linesInfo as FormGroup;
  }

  getLineName(index: number): string {
    // index in the form field starts at 1 instead of 0 so we need the + 1
    const type = this.linesInfo.controls[`typeLine${index + 1}`].value;

    return type === "Phone line" ? `Line ${index + 1}` : `BLF ${index + 1}`;
  }

  handleLineChange(event: MatRadioChange, index: number): void {
    const callWaitingControl = this.linesInfo.controls[`callWaitingLine${index + 1}`];
    if (event.value === "Busy lamp") {
      callWaitingControl.setValue(false);
      callWaitingControl.disable();
    } else if (event.value === "Phone line" && this.phoneFeatures.Concurrent) {
      callWaitingControl.enable();
      callWaitingControl.setValue(this.phoneFeatures.Concurrent);
    }
  }

  /** get error message for the first line */
  getLineError(index: number): string {
    if (
      this.linesInfo.controls[`nameLine${index}`] &&
      this.linesInfo.controls[`nameLine${index}`].hasError("duplicateKey")
    ) {
      return "This user is already assigned to a line of this type.";
    }
    if (
      this.linesInfo.controls.nameLine1 &&
      this.linesInfo.controls.nameLine1.hasError("required")
    ) {
      return "A user must be assigned to Line 1";
    }
    return "";
  }

  getPricing(): void {
    this.resourceService.accountResourceBrowse();
    this.unsubscriber.add(
      this.resourceService.state
        .pipe(
          filter(state => !state.loading),
          map(state => {
            return Object.values(state.state);
          })
        )
        .pipe(
          map(resourcesArray => {
            return resourcesArray.find(res => res.name === "BusyLampField");
          })
        )
        .subscribe(res => {
          this.blfPricing = res?.monthlyRecurringCharge || "undetermined";
          this.cdRef.markForCheck();
        })
    );
  }

  getPricingText(): string {
    if (this.userUnlimited) {
      return "Included in your current plan.";
    }
    return `$${this.blfPricing} per month`;
  }

  nextStep(): void {
    if (this.formGroup.valid) {
      if (this.stepper.selected) this.stepper.selected.completed = true;
      this.stepper.next();
    } else {
      this.formGroup.markAllAsTouched();
      this.formGroup.updateValueAndValidity();
    }
  }

  backStep(): void {
    if (this.formGroup.valid) {
      this.stepper.previous();
    } else {
      this.formGroup.markAllAsTouched();
      this.formGroup.updateValueAndValidity();
    }
  }

  toggleBLF(event: MatSlideToggleChange): void {
    if (this.formGroup.controls.blf.value) {
      const confirmModal = this.dialog.open(ModalMaterialComponent, {
        panelClass: ["mat-typography", "onsip-dialog-universal-style"],
        data: {
          title: "Enable Busy Lamp Field",
          message:
            "Selecting Busy Lamp Field applies the feature on all phones on your PBX that support it. Your account will be charged $19.95/month for this feature. Are you sure you want to enable Busy Lamp Field on your PBX? ",
          primaryBtnText: "OK",
          primaryBtnFlat: true,
          secondaryBtnText: "Cancel",
          noSentenceCaseTitle: true
        }
      });

      this.unsubscriber.add(
        confirmModal
          .afterClosed()
          .pipe(take(1))
          .subscribe(res => {
            if (res && res.doPrimaryAction) {
              this.organizationEditBusyLampFieldSave(event.checked);
            } else {
              this.formGroup.controls.blf.setValue(false);
            }
          })
      );
    } else {
      this.organizationEditBusyLampFieldSave(event.checked);
    }
  }

  submit(): void {
    if (this.formGroup.valid) {
      this.loadingStateComplete$.next(false);
      const formGroupValue = this.formGroup.getRawValue();
      const newPhoneParams: PhoneAddRequestParams = {
        MacAddress: formGroupValue.macAddress,
        Make: formGroupValue.make,
        Model: formGroupValue.model,
        GmtOffset: parseFloat(formGroupValue.timezone) * 3600,
        Directory: formGroupValue.directory,
        EasyTransfer: formGroupValue.easyTransfer,
        NatKeepalive: formGroupValue.natKeepalive,
        WebInterface: formGroupValue.webInterface,
        Intercom: formGroupValue.intercom,
        OrganizationWebPassword: formGroupValue.orgPassword,
        EndToEndEncryption: formGroupValue.endToEndEncryption,
        Digitmap: formGroupValue.digitMap
      };

      this.phoneService.phoneAdd(newPhoneParams).then(result => {
        if (result.status === "success") {
          const newPhone = Object.values(result.data)[0];
          this.newPhoneId = newPhone.phoneId;
          this.additionalApis(this.linesInfo.getRawValue());
        } else {
          this.snackbar.openSnackBar(result.data.message, "error");
          this.phoneService.clearErrors();
        }
        this.loadingStateComplete$.next(true);
      });
    }
  }

  private organizationEditBusyLampFieldSave(checked: boolean) {
    this.orgService
      .organizationEditBusyLampField(checked)
      .then(res => {
        if (res.status !== "error") {
          this.handleSuccessOrgEdit(checked);
        } else {
          const error = res.status === "error" ? res.data.message : "";
          this.handleErrorOrgEdit(error);
        }
      })
      .catch(() => {
        this.handleErrorOrgEdit();
      });
  }

  private handleErrorOrgEdit(error?: string) {
    this.showErrorSnackBar(error);
    this.orgService.clearErrors();
  }

  private handleSuccessOrgEdit(checked: boolean) {
    this.snackbar.openSnackBar(
      "Busy lamp field is " + (checked ? "enabled." : "disabled."),
      "success"
    );
  }

  private additionalApis(formGroupValue: Record<string, string | boolean>): void {
    const parsedLinesObject: { [key: string]: Partial<LineInfo> } = {};
    Object.entries(formGroupValue).forEach(([key, value]) => {
      if (key.includes("Line")) {
        const line = key.replace(/\D/g, "");
        if (!parsedLinesObject[line]) parsedLinesObject[line] = { line };
        if (key.includes("name")) {
          parsedLinesObject[line] = {
            ...parsedLinesObject[line],
            ...{ aor: value as LineInfo["aor"] }
          };
        }
        if (key.includes("type")) {
          parsedLinesObject[line] = {
            ...parsedLinesObject[line],
            ...{ type: value as LineInfo["type"] }
          };
        }
        if (key.includes("callWaiting")) {
          parsedLinesObject[line] = {
            ...parsedLinesObject[line],
            ...{ enabled: (value ? "enabled" : "disabled") as LineInfo["enabled"] }
          };
        }
      }
    });

    const assignedLines = Object.values(parsedLinesObject).filter(lines => lines.aor !== "");
    // check the number of lines are assigned. If number is zero, then we want to run phoneDeassignAddress because
    // phoneAssignAddressBulk requires at least one assigned address
    let request;
    if (!assignedLines.length) {
      request = this.phoneService.phoneDeassignAddress({ PhoneId: this.newPhoneId });
    } else {
      const formatParams: Record<string, ParameterValue> = {};
      Object.values(assignedLines).forEach((lines, index) => {
        formatParams[`Addresses[${index}][Line]`] = lines.line;
        formatParams[`Addresses[${index}][Address]`] = lines.aor;
        if (lines.type === "Phone line") {
          formatParams[`Addresses[${index}][CallsPerLine]`] = lines.enabled === "enabled" ? 0 : 1;
        } else if (lines.type === "Busy lamp") {
          formatParams[`Addresses[${index}][BusyLampField]`] = true;
          formatParams[`Addresses[${index}][SubscriberLine]`] = 1;
        }
      });

      request = this.phoneService.phoneAssignAddressBulk(this.newPhoneId, formatParams);
    }

    request
      .then(res => {
        if (res.status !== "error") {
          this.handleSuccessPhoneEdit();
        } else {
          const error = res.status === "error" ? res.data.message : "";
          this.handleErrorPhoneEdit(error);
        }
      })
      .catch(() => {
        this.handleErrorPhoneEdit();
      });
  }

  private handleSuccessPhoneEdit() {
    this.snackbar.openSnackBar(
      this.translate.instant("ONSIP_I18N.YOUR_CHANGES_HAVE_BEEN_SAVED"),
      "success"
    );
    this.nextStep();
  }

  private handleErrorPhoneEdit(error?: string) {
    this.showErrorSnackBar(error);
    this.phoneService.clearErrors();
  }

  /** generate formControls for lines that comes with phone minus expansion
   * nameFormControl - holds the user address the line is assigned to
   * typeFormControl - whether or not type is phone line or blf
   * callWaitingFormControl - whether or not call waiting is enabled - only if line is for phone
   */
  private getDefaultLinesFormControl(): void {
    // line can be either blf or phone if overlap is true
    const blfOverlap = this.phoneFeatures.BLFOverlap || false;
    // if overlap is true than total number of line is the maximum of Lines and BLF...
    // however when blf is greater than Lines - the total is blf + 1. That seems wrong in the old admin portal but i will leave that here for now
    // but if overlap is false, we need to add the number of Lines with BLF
    const totalLines =
      blfOverlap && this.phoneFeatures.BLF
        ? this.phoneFeatures.Lines < this.phoneFeatures.BLF
          ? this.phoneFeatures.BLF + 1
          : this.phoneFeatures.Lines
        : this.phoneFeatures.Lines + (this.phoneFeatures.BLF || 0);

    // to replace the instance variable, lineOptions, at the class level after the loop is finished
    const togglableOptions: Array<"phone" | "blf" | "both"> = [];

    // generate formcontrol for lines
    for (let i = 1; i <= totalLines; i++) {
      if (i === 1) {
        // First line will always be a phone line - following the old admin portal
        togglableOptions.push("phone");
      } else {
        // if blf is overlappable
        if (blfOverlap && this.phoneFeatures.BLF) {
          // following the old admin portal structure - phone only lines are at the top
          if (this.phoneFeatures.Lines < this.phoneFeatures.BLF) {
            // if phoneLines are less than blf than all lines less than phoneLines can be both
            if (i <= this.phoneFeatures.Lines) {
              togglableOptions.push("both");
            } else {
              togglableOptions.push("blf");
            }
          } else {
            // vice versa if blf is less than phonelines than the difference will give us phone only lines
            if (i <= this.phoneFeatures.Lines - this.phoneFeatures.BLF) {
              togglableOptions.push("phone");
            } else {
              togglableOptions.push("both");
            }
          }
        } else {
          // following old admin portal structure - phone lines go before blf lines
          if (i <= this.phoneFeatures.Lines) {
            togglableOptions.push("phone");
          } else {
            togglableOptions.push("blf");
          }
        }
      }
      // we will use the option to determine the default value of the type of line
      // phone and both will default to phone, blf will default to blf
      const option = togglableOptions[togglableOptions.length - 1];
      this.generateFormControl(i, option);
    }
    // update instance variables
    this.lineOptions = togglableOptions.map(type => ({ type, isExpansion: false }));
    this.totalLines = totalLines;
  }

  private generateFormControl(i: number, option: "phone" | "blf" | "both"): void {
    const defaultType = option === "blf" ? "Busy lamp" : "Phone line";
    const typeControl = this.linesInfo.controls[`typeLine${i}`];
    const callWaitingControl = this.linesInfo.controls[`callWaitingLine${i}`];
    // disable lines if it is not the first line and first line is not assigned

    const disabled = i > 1 && !this.firstLineAssigned;

    this.linesInfo.addControl(`nameLine${i}`, new FormControl({ value: "", disabled: disabled }));

    if (typeControl) {
      // do not reset type if the option allows for both types
      typeControl.setValue(option === "both" ? typeControl.value : defaultType);
    } else {
      this.linesInfo.addControl(
        `typeLine${i}`,
        new FormControl({ value: defaultType, disabled: disabled })
      );
    }
    if (callWaitingControl) {
      callWaitingControl.setValue(this.phoneFeatures.Concurrent);
      if (!disabled && this.phoneFeatures.Concurrent) {
        callWaitingControl.enable();
      } else {
        callWaitingControl.disable();
      }
    } else {
      this.linesInfo.addControl(
        `callWaitingLine${i}`,
        new FormControl({
          value: this.phoneFeatures.Concurrent,
          disabled: disabled || !this.phoneFeatures.Concurrent
        })
      );
    }
  }

  private addExpansionFormControls(): void {
    if (this.phoneFeatures.SideCar) {
      const expansionModuleLength =
        this.phoneFeatures.SideCar[0].BLF * this.phoneFeatures.SideCar[0].Multiple;
      for (let i = this.totalLines + 1; i <= this.totalLines + expansionModuleLength; i++) {
        this.generateFormControl(i, "blf");
      }
      // update instance variables
      this.lineOptions = this.lineOptions.concat(
        Array.from(Array(expansionModuleLength), () => ({ type: "blf", isExpansion: true }))
      );
      this.totalLines += expansionModuleLength;
    }
  }

  private removeExpansionFormControls(): void {
    if (this.phoneFeatures.SideCar) {
      const expansionModuleLength =
        this.phoneFeatures.SideCar[0].BLF * this.phoneFeatures.SideCar[0].Multiple;
      for (let i = this.totalLines; i > this.totalLines - expansionModuleLength; i--) {
        this.deleteExpansionFormControl(i);
      }
      // update instance variables
      this.totalLines -= expansionModuleLength;
      this.lineOptions = this.lineOptions.slice(0, this.totalLines);
    }
  }

  private deleteExpansionFormControl(i: number): void {
    this.linesInfo.removeControl(`nameLine${i}`);
    this.linesInfo.removeControl(`typeLine${i}`);
    this.linesInfo.removeControl(`callWaitingLine${i}`);
  }

  /** make sure the first line is assigned if any other line is also assigned
   * form will throw error on the first line if this is not true
   * however, if all lines are unassigned, first line is no longer required to be assigned
   */

  private getLinesFormGroupErrorState(): void {
    const allUnassigned = this.allLinesUnassigned();
    if (this.linesInfo.controls.nameLine1 && !allUnassigned) {
      this.linesInfo.controls.nameLine1.setValidators(Validators.required);
      if (!this.linesInfo.controls.nameLine1.value) {
        this.linesInfo.controls.nameLine1.setErrors({ required: true });
        this.linesInfo.controls.nameLine1.markAsTouched();
      }
      this.formGroup.updateValueAndValidity({ emitEvent: false });
    } else if (this.linesInfo.controls.nameLine1 && allUnassigned) {
      // eslint-disable-next-line no-null/no-null
      this.linesInfo.controls.nameLine1.setErrors(null);
      this.formGroup.updateValueAndValidity({ emitEvent: false });
    } else {
      this.formGroup.clearValidators();
      this.formGroup.updateValueAndValidity({ emitEvent: false });
    }
  }

  private setLinesEnabled(): void {
    for (let i = 1; i < this.totalLines + 1; i++) {
      if (this.linesInfo.controls[`nameLine${i}`]) {
        this.linesInfo.controls[`nameLine${i}`].enable();
        this.linesInfo.controls[`typeLine${i}`].enable();
        if (this.phoneFeatures.Concurrent) {
          this.linesInfo.controls[`callWaitingLine${i}`].enable();
        }
      }
    }
  }

  // disable lines > 1
  private setLinesDisabled(): void {
    for (let i = 2; i < this.totalLines + 1; i++) {
      if (this.linesInfo.controls[`nameLine${i}`]) {
        this.linesInfo.controls[`nameLine${i}`].disable({ onlySelf: true, emitEvent: false });
        this.linesInfo.controls[`typeLine${i}`].disable({ onlySelf: true, emitEvent: false });
        this.linesInfo.controls[`callWaitingLine${i}`].disable({
          onlySelf: true,
          emitEvent: false
        });
      }
    }
  }

  private allLinesUnassigned(): boolean {
    return Object.keys(this.linesInfo.controls)
      .filter(key => key.includes("nameLine"))
      .every(key => this.linesInfo.controls[key].value === "");
  }

  private showErrorSnackBar(error?: string) {
    error =
      error ||
      (this.translate.instant("ONSIP_I18N.THERE_WAS_AN_ERROR_SAVING_YOUR_INFORMATION") as string);
    this.snackbar.openSnackBar(error, "error");
  }

  private checkDuplicateLinesFormGroup() {
    const lineInfoValues = this.linesInfo.getRawValue();
    const lineInfoMap = new Map();
    for (let i = 1; i <= this.totalLines; i++) {
      // skip if line is empty
      if (!lineInfoValues[`nameLine${i}`]) {
        continue;
      }
      const lineInfoKey =
        lineInfoValues[`nameLine${i}`] +
        lineInfoValues[`typeLine${i}`] +
        lineInfoValues[`callWaitingLine${1}`];
      if (lineInfoMap.has(lineInfoKey)) {
        // set line to be in error and then break loop
        this.linesInfo.controls[`nameLine${i}`].setErrors({ duplicateKey: true });
        break;
      } else {
        lineInfoMap.set(lineInfoKey, true);
        // clear any old duplicated key
        // eslint-disable-next-line no-null/no-null
        this.linesInfo.controls[`nameLine${i}`].setErrors(null);
      }
    }
    this.formGroup.updateValueAndValidity({ emitEvent: false });
  }
}
